// PERM V2 — hook + componente PermGate + helper can()
// Reaproveita /api/perm/me. ADMIN legado sempre retorna can()=true.
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';

const PermContext = createContext(null);

export function PermV2Provider({ children }) {
  const [perms, setPerms] = useState(null); // { acoes: {key: bool}, is_admin_bypass: bool, ... }
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/perm/me');
      setPerms(res.data);
    } catch (e) {
      setPerms({ acoes: {}, is_admin_bypass: false, error: e?.response?.data?.detail || String(e) });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    // Só busca se houver token
    if (typeof window !== 'undefined' && localStorage.getItem('token')) {
      refresh();
    } else {
      setLoading(false);
    }
    // Recarrega as permissões após login/logout (evento disparado no Login)
    const onAuthChange = () => {
      if (localStorage.getItem('token')) refresh();
      else setPerms(null);
    };
    window.addEventListener('auth-change', onAuthChange);
    return () => window.removeEventListener('auth-change', onAuthChange);
  }, [refresh]);

  const can = useCallback((module, verb) => {
    if (!perms) return false;
    if (perms.is_admin_bypass) return true;
    const key = `${module}.${verb}`;
    return perms.acoes?.[key] === true;
  }, [perms]);

  const canAny = useCallback((module, verbs = []) => {
    if (!perms) return false;
    if (perms.is_admin_bypass) return true;
    return verbs.some(v => perms.acoes?.[`${module}.${v}`] === true);
  }, [perms]);

  // Gate global do financeiro: perfis pedagógicos (Gestão DNA) nunca veem valores.
  const financeiroVisivel = React.useMemo(() => {
    if (!perms) return false;
    if (perms.is_admin_bypass) return true;
    return perms.acoes?.['financeiro.visualizar'] === true
      || perms.acoes?.['dnapay.visualizar'] === true
      || perms.acoes?.['dados_sensiveis.financeiro'] === true;
  }, [perms]);

  return (
    <PermContext.Provider value={{ perms, loading, refresh, can, canAny, financeiroVisivel }}>
      {children}
    </PermContext.Provider>
  );
}

export function usePermV2() {
  const ctx = useContext(PermContext);
  if (!ctx) {
    // Provider ausente — modo permissivo seguro (não quebra a tela em testes isolados)
    return {
      perms: null, loading: false, refresh: () => {},
      can: () => false, canAny: () => false, financeiroVisivel: true,
    };
  }
  return ctx;
}

// Gate de UI: oculta children se sem permissão.
// Uso: <PermGate module="festa_junina" verb="visualizar"><Btn/></PermGate>
export function PermGate({ module, verb, verbs, fallback = null, children }) {
  const { can, canAny, loading } = usePermV2();
  if (loading) return null;
  const ok = verbs ? canAny(module, verbs) : can(module, verb);
  return ok ? <>{children}</> : fallback;
}
